import { useState } from "react";
import { History, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

function fmt(iso) {
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
}

/**
 * "Last updated by <user> on <date>" for a wiki-editable listing, with an
 * expandable change log. When entityType/entityId are provided and the viewer is
 * an admin, each edit that carries a before-snapshot gets a one-click Revert.
 * Props: entity, entityType?, entityId?, onReverted?(freshEntity)
 */
export default function EditHistory({ entity, entityType, entityId, onReverted }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [reverting, setReverting] = useState(null);
    const history = Array.isArray(entity?.edit_history) ? entity.edit_history : [];
    if (!entity?.last_edited_by && history.length === 0) return null;
    const isAdmin = !!user?.is_admin;
    const canRevert = isAdmin && entityType && entityId;

    const revert = async (idx) => {
        setReverting(idx);
        try {
            const { data } = await api.post(`/moderation/${entityType}/${entityId}/revert`, { entry_index: idx });
            toast.success("Reverted to that version");
            onReverted?.(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not revert");
        } finally { setReverting(null); }
    };

    return (
        <div className="text-[11px] text-[#a0a0a0]" data-testid="edit-history">
            <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 hover:text-[#ff4500] transition-colors">
                <History className="h-3 w-3" />
                Last updated by <span className="text-white">{entity.last_edited_by}</span>
                {entity.last_edited_at && <> on {fmt(entity.last_edited_at)}</>}
                {history.length > 0 && <span className="underline ml-1">view history</span>}
            </button>
            {open && history.length > 0 && (
                <div className="mt-2 border border-[#2a2a2a] bg-[#0f0f0f] divide-y divide-[#2a2a2a]" data-testid="edit-history-list">
                    {history.map((h, i) => (
                        <div key={i} className="px-3 py-2 flex items-start justify-between gap-3" data-testid={`edit-history-entry-${i}`}>
                            <div>
                                <span className="text-white">{h.username || "a user"}</span> · {fmt(h.at)}
                                {h.revert_of !== undefined && <span className="text-[#ff4500]"> (revert)</span>}
                                {Array.isArray(h.fields) && h.fields.length > 0 && (
                                    <span className="text-[#666]"> — updated {h.fields.join(", ")}</span>
                                )}
                            </div>
                            {canRevert && h.before && Object.keys(h.before).length > 0 && (
                                <button
                                    type="button"
                                    data-testid={`revert-edit-${i}`}
                                    onClick={() => revert(i)}
                                    disabled={reverting === i}
                                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-[#ff4500] text-[#ff4500] text-[9px] uppercase tracking-[0.15em] hover:bg-[#ff4500] hover:text-white transition-colors disabled:opacity-50"
                                >
                                    <RotateCcw className="h-3 w-3" /> {reverting === i ? "…" : "Revert"}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
